import { axiosInstance, axiosPluginInstance } from "./axios";
import { ItemKind } from "dto/IKindItems";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IConnectResponse } from "dto/Plugin/IConnectResponse";
import { ICertificate } from "dto/ICertificate";
import {
  IScannerDevice,
  IScanStartRequest,
  IScanStartResponse,
  IScanJobStatus,
  EScanJobState,
} from "dto/Plugin/scan";
import {
  ICouncilProtocolRequest,
  ICouncilProtocolResponse,
} from "dto/Plugin/councilProtocol";
import {
  ICouncilStreamState,
  IObsOutputRequest,
  IObsSceneRequest,
  IObsVotingSceneRequest,
  IOsdWriteRequest,
  IPtzPresetRequest,
  IPtzTestRequest,
  IPtzTestResponse,
} from "dto/Plugin/councilStream";

const queryKeys = {
  getVersion: "pluginService.getVersion",
  actualVersion: "pluginService.actualVersion",
};

const actualVersion = async () => {
  const r = await axiosInstance.get("/plugin/version");

  return r.data ?? [];
};

const useActualVersion = () => {
  return useCustomQuery(queryKeys.actualVersion, () => actualVersion());
};

export interface IVersion {
  Platform: string;
  Version: string;
  IsConnected?: boolean;
}

const getVersion = async (): Promise<IVersion> => {
  const response = await axiosPluginInstance.get("/api/version");
  return response.data ?? [];
};

const useGetVersion = (retry?: boolean) => {
  return useCustomQuery(queryKeys.getVersion, () => getVersion(), {
    retry: retry ?? 3,
  });
};

const useGetVersionMutated = () => {
  return useCustomMutation(getVersion);
};

export interface IOpenEditor {
  Id: string;
  Kind: ItemKind;
  VersionId?: string;
}

const openEditor = async (payload: IOpenEditor) => {
  return await axiosPluginInstance.post("/api/openEditor", payload);
};

const useOpenEditor = () => {
  return useCustomMutation(openEditor);
};

export interface IPluginCreateConnection {
  AppId: string;
  Version: string;
}

const createConnection = async (
  payload: IPluginCreateConnection,
): Promise<{ data: IConnectResponse }> => {
  return await axiosInstance.post("/Plugin/Connect", payload);
};

const useCreateConnection = () => {
  return useCustomMutation(createConnection);
};

export interface IPluginSetConnection {
  PrivateKey: string;
  PublicKey: string;
  Token: string;
  RefreshToken: string;
  Url: string;
}

const setConnection = async (payload: IPluginSetConnection) => {
  return await axiosPluginInstance.post("/api/connect", payload);
};

const useSetConnection = () => {
  return useCustomMutation(setConnection);
};

export interface IPluginCheckPreview {
  AttachmentId: string;
  DocumentId: string;
  FileName: string;
  Kind: number;
}

const checkPreview = async (payload: IPluginCheckPreview) => {
  return await axiosPluginInstance.post("/api/checkPreview", payload);
};

const useCheckPreview = () => {
  return useCustomMutation(checkPreview);
};

export interface IPluginPreview {
  AttachmentId?: string;
  Content: string;
  ContentType?: string | null;
  DocumentId?: string;
  FileName: string;
  Kind?: number;
}

const preview = async (payload: IPluginPreview) => {
  return await axiosPluginInstance.post("/api/preview", payload);
};

const usePreview = () => {
  return useCustomMutation(preview);
};

export interface IVerifySignature {
  FileName: string;
  Content: string;
  ContentType: string;
  Xades?: string;
}

const verifySignature = (payload: IVerifySignature) => {
  return axiosPluginInstance.post("/api/verify", payload);
};

const useVerifySignature = () => {
  return useCustomMutation(verifySignature);
};

const getCertificates = (): Promise<{ data: ICertificate[] }> => {
  return axiosPluginInstance.get("/api/certificates");
};

const useGetCerificates = () => {
  return useCustomQuery("certs", () => getCertificates());
};

export interface ISign {
  CertificateSerialNumber: string;
  Content: string;
  ContentType: string;
  FileName: string;
  GenerateReport: boolean;
  FieldAppearance?: ISignFieldAppearance;
  ForceXades?: boolean;
  /**
   * XML: Id elementu do podpisania — XAdES nad jednym elementem (Reference URI="#Id",
   * podpis pod korzeniem, bez enveloped). Wniosek o zaangażowanie: „Ak-…” / „metrykaZaangazowania”.
   */
  XmlElementId?: string;
}

export interface ISignFieldAppearance {
  Page: number;
  X: number;
  Y: number;
  Width: number;
  Height: number;
  PageWidth: number;
  PageHeight: number;
  Logo?: string;
}

const signDocument = (payload: ISign) => {
  return axiosPluginInstance.post("/api/sign", payload);
};

const useSignDocument = () => {
  return useCustomMutation(signDocument);
};

// --- Generowanie sparametryzowanych pism per odbiorca (wypełnianie DOCVARIABLE + render PDF) ---

export interface IGenerateLettersReceiver {
  ReceiverId: string;
  /** Klucz publiczny do zaszyfrowania PDF (base64). Pusty = klucz dokumentu (zachowany oryginał autora). */
  PublicKey: string;
  Variables: Record<string, string>;
  RemoveParagraphsForVariables: string[];
  /** PDF wgrywany BEZ szyfrowania (plaintext) — dla kanałów zewnętrznych (e-mail / rządowe e-Doręczenia / PUH). */
  Plaintext?: boolean;
}

export interface IGenerateLettersRequest {
  DocumentId: string;
  Kind: ItemKind;
  Receivers: IGenerateLettersReceiver[];
  /**
   * Własny podpis kopii: gdy ustawione, wtyczka po wyrenderowaniu każdego PDF
   * podpisuje go tym certyfikatem przy współrzędnych `FieldAppearance` (te same
   * dla wszystkich odbiorców — pozycję ustala się raz na piśmie bazowym), a
   * dopiero potem szyfruje (kluczem dokumentu) / wgrywa. Bez tego pola — sam render.
   */
  CertificateSerialNumber?: string;
  FieldAppearance?: ISignFieldAppearance;
}

export interface IGenerateLettersResult {
  ReceiverId: string;
  Uri: string;
  Success: boolean;
  Error?: string;
  /** true, gdy wtyczka podpisała wyrenderowany PDF (ścieżka własnego podpisu). */
  Signed?: boolean;
}

export interface IGenerateLettersResponse {
  Results: IGenerateLettersResult[];
}

const generateLetters = async (
  payload: IGenerateLettersRequest,
): Promise<{ data: IGenerateLettersResponse }> => {
  return await axiosPluginInstance.post("/api/generateLetters", payload);
};

const useGenerateLetters = () => {
  return useCustomMutation(generateLetters);
};

// --- Skaner (obsługa przez sidecar WIA wtyczki) ---

const getScanners = async (): Promise<IScannerDevice[]> => {
  // Timeout, bo instancja pluginowa domyślnie go nie ma — brak/zawieszona wtyczka
  // ma szybko dać błąd (→ PluginNotExist), a nie wisieć na "Wyszukiwanie skanerów…".
  const response = await axiosPluginInstance.get("/api/scanners", {
    timeout: 8000,
  });
  return response.data ?? [];
};

const useGetScanners = (enabled = true) => {
  return useCustomQuery("pluginService.scanners", () => getScanners(), {
    enabled,
    staleTime: 0,
    cacheTime: 0,
    // Bez retry — brak wtyczki lub stara wersja (404 na /api/scanners) mają od razu
    // pokazać PluginNotExist, zamiast retry'ować 3× z backoffem.
    retry: false,
  });
};

const startScan = async (
  payload: IScanStartRequest,
): Promise<IScanStartResponse> => {
  const response = await axiosPluginInstance.post("/api/scan/jobs", payload);
  return response.data;
};

const useStartScan = () => {
  return useCustomMutation(startScan);
};

const getScanJob = async (jobId: string): Promise<IScanJobStatus> => {
  const response = await axiosPluginInstance.get(`/api/scan/jobs/${jobId}`);
  return response.data;
};

// Poll co ~750ms dopóki zadanie skanuje; strony dochodzą na bieżąco.
const useScanJob = (jobId: string | undefined, enabled: boolean) => {
  return useCustomQuery(
    ["pluginService.scanJob", jobId],
    () => getScanJob(jobId as string),
    {
      enabled: enabled && !!jobId,
      refetchInterval: (data) =>
        data?.State === EScanJobState.Scanning ? 750 : false,
      refetchIntervalInBackground: true,
    },
  );
};

// Pełny JPEG strony (base64 w JSON) — dociągany leniwie przy składaniu PDF / podglądzie.
const getScanPage = async (jobId: string, index: number): Promise<Blob> => {
  const response = await axiosPluginInstance.get(
    `/api/scan/jobs/${jobId}/pages/${index}`,
  );
  const base64: string = response.data?.Content ?? "";
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: "image/jpeg" });
};

const cancelScan = async (jobId: string) => {
  return await axiosPluginInstance.post(`/api/scan/jobs/${jobId}/cancel`);
};

const useCancelScan = () => {
  return useCustomMutation((jobId: string) => cancelScan(jobId));
};

const deleteScanJob = async (jobId: string) => {
  return await axiosPluginInstance.delete(`/api/scan/jobs/${jobId}`);
};

const useDeleteScanJob = () => {
  return useCustomMutation((jobId: string) => deleteScanJob(jobId));
};

// --- Biuro rady: transmisja obrad (OBS Studio, napisy OSD, kamery PTZ) ---
//
// Wtyczka jest tu wykonawcą tego, co w Magistracie robił program WPF na sali obrad.
// Konfigurację (adres i hasło OBS, poświadczenia kamer) wtyczka pobiera z backendu
// SAMA — panel wskazuje kamerę i preset po identyfikatorze, więc żaden sekret
// transmisji nie przechodzi przez przeglądarkę.

const STREAM_TIMEOUT = 6000;
/** Łączenie i uruchamianie transmisji trwa dłużej niż zwykłe wywołanie. */
const STREAM_SLOW_TIMEOUT = 20000;

const getCouncilStreamState = async (): Promise<ICouncilStreamState> => {
  const response = await axiosPluginInstance.get("/api/council/stream/state", {
    timeout: STREAM_TIMEOUT,
  });
  return response.data;
};

/**
 * Stan transmisji odpytujemy cyklicznie, bo OBS zmienia się poza aplikacją:
 * realizator może przełączyć scenę albo zatrzymać nadawanie ręcznie.
 */
const useCouncilStreamState = (enabled: boolean, refetchInterval?: number) =>
  useCustomQuery(
    "pluginService.councilStreamState",
    () => getCouncilStreamState(),
    {
      enabled,
      refetchInterval: enabled ? refetchInterval : false,
      refetchIntervalInBackground: true,
      staleTime: 0,
      cacheTime: 0,
      // Bez wtyczki (albo ze starszą, która nie zna tej ścieżki) chcemy od razu
      // pokazać „brak wtyczki", a nie trzy próby z odczekiwaniem.
      retry: false,
    },
  );

const councilStreamAction = async (
  path: string,
  payload?: unknown,
  timeout = STREAM_TIMEOUT,
): Promise<ICouncilStreamState> => {
  const response = await axiosPluginInstance.post(
    `/api/council/stream/${path}`,
    payload ?? {},
    { timeout },
  );
  return response.data;
};

const connectCouncilStream = () =>
  councilStreamAction("connect", undefined, STREAM_SLOW_TIMEOUT);

const disconnectCouncilStream = () => councilStreamAction("disconnect");

/** Po zapisie ustawień Biura rady wtyczka musi pobrać konfigurację od nowa. */
const reloadCouncilStream = () => councilStreamAction("reload");

const runObs = () => councilStreamAction("run-obs", undefined, STREAM_SLOW_TIMEOUT);

const setObsScene = (payload: IObsSceneRequest) =>
  councilStreamAction("scene", payload);

const setObsOutput = (payload: IObsOutputRequest) =>
  councilStreamAction("output", payload, STREAM_SLOW_TIMEOUT);

const setObsVotingScene = (payload: IObsVotingSceneRequest) =>
  councilStreamAction("voting", payload);

const writeOsd = (payload: IOsdWriteRequest) =>
  councilStreamAction("osd", payload);

const gotoCameraPreset = (payload: IPtzPresetRequest) =>
  councilStreamAction("camera", payload, 10000);

/**
 * Składa protokół sesji do wskazanego, pustego dokumentu edytowalnego.
 * Dokument zakłada aplikacja (POST editor/document) razem z kluczem obiektu — wtyczka
 * pobiera przebieg sesji z backendu, buduje DOCX i wgrywa go jako treść dokumentu.
 *
 * Limit czasu jest hojny: protokół obszernej sesji to kilkaset akapitów i tabele
 * głosowań, a wtyczka składa go synchronicznie.
 */
const generateCouncilProtocol = async (
  payload: ICouncilProtocolRequest,
): Promise<ICouncilProtocolResponse> => {
  const response = await axiosPluginInstance.post(
    "/api/council/protocol",
    payload,
    { timeout: 120000 },
  );
  return response.data;
};

const testCamera = async (payload: IPtzTestRequest): Promise<IPtzTestResponse> => {
  const response = await axiosPluginInstance.post(
    "/api/council/stream/camera/test",
    payload,
    { timeout: 12000 },
  );
  return response.data;
};

export default {
  useGetVersion,
  useGetVersionMutated,
  // Surowa funkcja — sprawdzenie stanu połączenia przed imperatywnym wywołaniem wtyczki.
  getVersion,
  useOpenEditor,
  useCreateConnection,
  useSetConnection,
  useCheckPreview,
  usePreview,
  // Surowa konwersja do PDF — pętla wydruku pism (dokument źródłowy docx).
  preview,
  useVerifySignature,
  useGetCerificates,
  signDocument,
  useSignDocument,
  useActualVersion,
  useGetScanners,
  useStartScan,
  useScanJob,
  useCancelScan,
  useDeleteScanJob,
  getScanPage,
  useGenerateLetters,
  // Surowa funkcja do imperatywnego wywołania w pętli generacji pism.
  generateLetters,
  // Biuro rady — transmisja. Poza hookami wystawiamy surowe funkcje, bo panel
  // obrad woła je jako efekt uboczny akcji prowadzącego, a nie z renderu.
  useCouncilStreamState,
  getCouncilStreamState,
  connectCouncilStream,
  disconnectCouncilStream,
  reloadCouncilStream,
  runObs,
  setObsScene,
  setObsOutput,
  setObsVotingScene,
  writeOsd,
  gotoCameraPreset,
  testCamera,
  generateCouncilProtocol,
};
